/**
 * ErrorBoundary Component
 * Catches React errors and reports them to the error tracking API
 *
 * @shared - Used by both client and admin
 */

import React, { Component } from 'react';
import { Box, Button, Typography, Paper, Stack } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';

// Default API URL - can be overridden via props
const DEFAULT_API_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Error Boundary wrapper component
 * Catches JavaScript errors anywhere in the child component tree
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({ errorInfo });

    // Report to error tracking API
    this.reportError(error, errorInfo);
  }

  async reportError(error, errorInfo) {
    try {
      const { source = 'client', apiUrl = DEFAULT_API_URL } = this.props;

      const response = await fetch(`${apiUrl}/errors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: error.message || 'Unknown error',
          stack: error.stack,
          type: 'react_error',
          source,
          url: window.location.href,
          componentStack: errorInfo?.componentStack,
          context: {
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            viewport: {
              width: window.innerWidth,
              height: window.innerHeight,
            },
          },
        }),
      });

      if (response.ok) {
        const data = await response.json();
        this.setState({ errorId: data.groupId });
      }
    } catch (reportError) {
      // Silently fail - don't cause another error while reporting
      console.error('Failed to report error:', reportError);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    const { hasError, error, errorId } = this.state;
    const {
      children,
      fallback,
      errorTitle = 'Что-то пошло не так',
      errorMessage = 'Произошла непредвиденная ошибка. Мы уже получили уведомление и работаем над исправлением.',
      errorIdLabel = 'ID ошибки:',
      retryText = 'Повторить',
      homeText = 'На главную',
    } = this.props;

    if (hasError) {
      // Custom fallback if provided
      if (fallback) {
        return fallback({ error, errorId, onRetry: this.handleRetry });
      }

      // Default error UI
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            bgcolor: 'background.default',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              maxWidth: 500,
              width: '100%',
              p: 4,
              textAlign: 'center',
            }}
          >
            <ErrorOutlineIcon
              sx={{
                fontSize: 64,
                color: 'error.main',
                mb: 2,
              }}
            />

            <Typography variant="h5" gutterBottom fontWeight="bold">
              {errorTitle}
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {errorMessage}
            </Typography>

            {errorId && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 3, fontFamily: 'monospace' }}
              >
                {errorIdLabel} {errorId}
              </Typography>
            )}

            {import.meta.env.DEV && error && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: 'grey.50',
                  textAlign: 'left',
                  overflow: 'auto',
                  maxHeight: 200,
                }}
              >
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    m: 0,
                  }}
                >
                  {error.message}
                  {'\n\n'}
                  {error.stack}
                </Typography>
              </Paper>
            )}

            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={this.handleRetry}
                sx={{ minWidth: 120, minHeight: 44 }}
              >
                {retryText}
              </Button>
              <Button
                variant="outlined"
                startIcon={<HomeIcon />}
                onClick={this.handleGoHome}
                sx={{ minWidth: 120, minHeight: 44 }}
              >
                {homeText}
              </Button>
            </Stack>
          </Paper>
        </Box>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
